/**
 * AlertRecipientsDialog — who gets told when something needs approving.
 *
 * A named list, picked from the admins who actually hold finance.approve. If
 * the list is empty every eligible approver is alerted, so a queued expense
 * never sits unseen.
 */
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const call = async (path, opts = {}) => {
  const r = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { ...authHeaders(), 'Content-Type': 'application/json', ...opts.headers },
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.detail || 'Request failed');
  return body;
};

export const AlertRecipientsDialog = ({ open, onClose }) => {
  const qc = useQueryClient();
  const [picked, setPicked] = useState({});   // email → { name, email, phone, admin_id }
  const [enabled, setEnabled] = useState(true);
  const [digest, setDigest] = useState(true);

  const { data, isLoading } = useQuery({
    queryKey: ['approval-alert-settings'],
    queryFn: () => call('/api/bank/approval-alerts/settings'),
    enabled: open,
    retry: false,
  });

  useEffect(() => {
    if (!data) return;
    setEnabled(data.enabled);
    setDigest(data.digest_enabled);
    const seed = {};
    (data.recipients || []).forEach((r) => { seed[r.email] = r; });
    setPicked(seed);
  }, [data]);

  const save = useMutation({
    mutationFn: () => call('/api/bank/approval-alerts/settings', {
      method: 'PUT',
      body: JSON.stringify({
        enabled, digest_enabled: digest, recipients: Object.values(picked),
      }),
    }),
    onSuccess: () => {
      toast.success('Alert list saved');
      qc.invalidateQueries({ queryKey: ['approval-alert-settings'] });
      onClose?.();
    },
    onError: (e) => toast.error(e.message),
  });

  const digestNow = useMutation({
    mutationFn: () => call('/api/bank/approval-alerts/digest', { method: 'POST' }),
    onSuccess: (res) => toast.success(res.alerted
      ? `Reminder sent to ${res.alerted} approver${res.alerted === 1 ? '' : 's'}`
      : 'Nothing is stale enough to chase'),
    onError: (e) => toast.error(e.message),
  });

  const eligible = data?.eligible_approvers || [];
  const toggle = (person) => setPicked((p) => {
    const next = { ...p };
    if (next[person.email]) delete next[person.email];
    else next[person.email] = person;
    return next;
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg max-h-[85vh] overflow-y-auto"
                     data-testid="alert-recipients-dialog">
        <DialogHeader>
          <DialogTitle>Approval alerts</DialogTitle>
          <DialogDescription>
            Email always, WhatsApp when a number is on file, and an in-app notification.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="alerts-on" className="text-sm">Alert the moment something queues</Label>
            <Switch id="alerts-on" checked={enabled} onCheckedChange={setEnabled}
                    data-testid="alerts-enabled-switch" />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="digest-on" className="text-sm">Daily chase-up at 08:00</Label>
            <Switch id="digest-on" checked={digest} onCheckedChange={setDigest}
                    data-testid="alerts-digest-switch" />
          </div>

          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Recipients</p>
            {isLoading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
            {!isLoading && Object.keys(picked).length === 0 && (
              <p className="text-[11px] text-amber-700 mb-2" data-testid="alerts-fallback-note">
                Nobody picked — every approver is alerted until you choose.
              </p>
            )}
            <ul className="space-y-2">
              {eligible.map((p) => (
                <li key={p.email} className="flex items-start gap-2"
                    data-testid={`alert-recipient-${p.email}`}>
                  <Checkbox
                    checked={!!picked[p.email]}
                    onCheckedChange={() => toggle(p)}
                    className="mt-0.5"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-800 truncate">{p.name || p.email}</p>
                    <p className="text-[11px] text-slate-400 truncate">{p.email}</p>
                  </div>
                  {picked[p.email] && (
                    <Input
                      value={picked[p.email].phone || ''}
                      onChange={(e) => setPicked((s) => ({
                        ...s, [p.email]: { ...s[p.email], phone: e.target.value },
                      }))}
                      placeholder="WhatsApp number"
                      className="h-8 w-32 sm:w-40 text-xs shrink-0"
                      data-testid={`alert-phone-${p.email}`}
                    />
                  )}
                </li>
              ))}
              {!isLoading && eligible.length === 0 && (
                <li className="text-xs text-slate-400">No admin holds finance approval yet.</li>
              )}
            </ul>
          </div>

          <Badge variant="outline" className="text-[10px]">
            {Object.keys(picked).length} selected
          </Badge>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" className="w-full sm:w-auto"
                  disabled={digestNow.isPending}
                  data-testid="send-digest-now"
                  onClick={() => digestNow.mutate()}>
            <Send className="h-3.5 w-3.5 mr-1.5" /> Chase now
          </Button>
          <Button className="w-full sm:w-auto" disabled={save.isPending}
                  data-testid="save-alert-recipients"
                  onClick={() => save.mutate()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AlertRecipientsDialog;
